import { useState, useEffect } from 'react';
import { X, Calendar, FileText, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from './ui/use-toast';
import { Machine, PlanogramConfig, SKUAvailability } from '../types/map';
import { formatCurrency } from '../lib/utils';
import BeforeAfterSlider from './BeforeAfterSlider';

interface TenderRequestModalProps {
  selectedMachines: Array<{ machine: Machine; airport: any }>;
  onClose: () => void;
  onSuccess: () => void;
}

export function TenderRequestModal({ selectedMachines, onClose, onSuccess }: TenderRequestModalProps) {
  const [reservationType, setReservationType] = useState('');
  const [customReservationType, setCustomReservationType] = useState('');
  const [notes, setNotes] = useState('');
  const [requestedStart, setRequestedStart] = useState('');
  const [requestedEnd, setRequestedEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Get launch date and timeframe from the first machine (all selected machines should have the same values)
  const firstMachine = selectedMachines.length > 0 ? selectedMachines[0].machine : null;
  const launchDate = firstMachine?.tenderLaunchDate || null;
  const timeframe = firstMachine?.tenderTimeframe || null;
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [availability, setAvailability] = useState<SKUAvailability | null>(null);
  const [planogramConfig, setPlanogramConfig] = useState<PlanogramConfig>({ rows: 6, columns: 9, positions: [] });
  const [pricePerSKU, setPricePerSKU] = useState<number>(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedMachineForDetails, setSelectedMachineForDetails] = useState<{ machine: Machine; airport: any } | null>(null);
  const [skuAvailability, setSkuAvailability] = useState<SKUAvailability | null>(null);
  const { toast } = useToast();

  // Load SKU availability for the first machine
  useEffect(() => {
    if (selectedMachines.length > 0) {
      const firstMachine = selectedMachines[0].machine;
      
      // Parse planogram config
      if (firstMachine.planogramConfig) {
        try {
          const config = typeof firstMachine.planogramConfig === 'string' 
            ? JSON.parse(firstMachine.planogramConfig)
            : firstMachine.planogramConfig;
          setPlanogramConfig(config);
        } catch (e) {
          console.error('Error parsing planogram config:', e);
        }
      }

      // Fetch availability
      fetch(`/api/brand/reservations/machines/${firstMachine.id}/sku-availability`)
        .then(res => {
          if (!res.ok) {
            if (res.status === 403) {
              throw new Error('Access denied. Please ensure you are logged in as a brand user.');
            }
            throw new Error(`Failed to fetch availability: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setAvailability(data);
          setApiError(null);
        })
        .catch(err => {
          console.error('Error fetching availability:', err);
          setAvailability(null);
          setApiError(err.message || 'Failed to load SKU availability');
        });

      // Fetch brand pricing
      fetch('/api/brand/reservations/pricing')
        .then(res => {
          if (!res.ok) {
            if (res.status === 403) {
              throw new Error('Access denied. Please ensure you are logged in as a brand user.');
            }
            throw new Error('Failed to fetch pricing');
          }
          return res.json();
        })
        .then(data => {
          if (data.tenderPricePerSKUPerMonth) {
            setPricePerSKU(data.tenderPricePerSKUPerMonth / 100); // Convert cents to pounds
          }
          setApiError(null);
        })
        .catch(err => {
          console.error('Error fetching brand pricing:', err);
          setApiError(err.message || 'Failed to load pricing information');
        });
    }
  }, [selectedMachines]);

  // Fetch SKU availability when viewing a machine in the details dialog
  useEffect(() => {
    if (selectedMachineForDetails?.machine.requiresTenderProcess && selectedMachineForDetails.machine.planogramConfig) {
      fetch(`/api/brand/reservations/machines/${selectedMachineForDetails.machine.id}/sku-availability`)
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          return null;
        })
        .then(data => {
          if (data) setSkuAvailability(data);
        })
        .catch(err => {
          console.error('Error fetching SKU availability:', err);
        });
    } else {
      setSkuAvailability(null);
    }
  }, [selectedMachineForDetails]);

  const renderStatusPill = (status: string) => {
    let label = status;
    let colorClasses = '';
    let showDot = false;

    if (status === 'Live/Available') {
      label = 'Live';
      colorClasses = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      showDot = true;
    } else if (status === 'Reserved') {
      label = 'Reserved';
      colorClasses = 'bg-amber-100 text-amber-700 border border-amber-200';
    } else if (status === 'Coming Soon') {
      label = 'Coming Soon';
      colorClasses = 'bg-blue-100 text-blue-700 border border-blue-200';
    } else {
      colorClasses = 'bg-slate-100 text-slate-700 border border-slate-200';
    }

    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${colorClasses}`}>
        {showDot && <span className="w-2 h-2 rounded-full bg-emerald-600"></span>}
        {label}
      </span>
    );
  };

  const toggleSku = (row: number, col: number) => {
    const key = `${row},${col}`;
    const newSelected = new Set(selectedSkus);
    
    // Check if this position is already allocated (only approved allocations block selection)
    const isAllocated = availability?.allocations?.some(
      alloc => alloc.rowIndex === row && alloc.columnIndex === col && alloc.status === 'approved'
    ) || false;
    
    if (isAllocated) {
      toast({
        title: 'Position Unavailable',
        description: 'This SKU position is already allocated',
        variant: 'destructive',
      });
      return;
    }
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedSkus(newSelected);
  };

  const getCategoryForPosition = (row: number, col: number): string | undefined => {
    return planogramConfig.positions.find(p => p.row === row && p.col === col)?.category;
  };

  const getCategoryColor = (category: string | undefined): string => {
    if (!category) return 'bg-gray-100';
    const colors: Record<string, string> = {
      'Energy Drinks': 'bg-yellow-100',
      'Snacks': 'bg-orange-100',
      'Beverages': 'bg-blue-100',
      'Healthy Options': 'bg-green-100',
      'Confectionery': 'bg-pink-100',
    };
    return colors[category] || 'bg-purple-100';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMachines.length) {
      toast({
        title: 'Error',
        description: 'No machines selected',
        variant: 'destructive',
      });
      return;
    }

    if (!reservationType) {
      toast({
        title: 'Error',
        description: 'Please select a reservation type',
        variant: 'destructive',
      });
      return;
    }

    if (reservationType === 'Custom' && !customReservationType.trim()) {
      toast({
        title: 'Error',
        description: 'Please specify your custom reservation type',
        variant: 'destructive',
      });
      return;
    }

    if (selectedSkus.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one SKU position',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      const machineIds = selectedMachines.map(m => m.machine.id);
      const finalReservationType = reservationType === 'Custom' ? customReservationType : reservationType;
      
      const skuSelections = Array.from(selectedSkus).map(key => {
        const [row, col] = key.split(',').map(Number);
        return { row, col };
      });

      const response = await fetch('/api/brand/reservations/tender-reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          machineIds,
          skuSelections,
          reservationType: finalReservationType,
          notes: notes || undefined,
          requestedStart: requestedStart || undefined,
          requestedEnd: requestedEnd || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create tender reservation request');
      }

      toast({
        title: 'Success',
        description: `Tender reservation request submitted for ${machineIds.length} machine(s) with ${selectedSkus.size} SKUs each`,
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting tender reservation request:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to submit tender reservation request',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 md:left-60 md:inset-y-0 md:right-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Request New Location Machines</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Selected Machines ({selectedMachines.length})</h3>
            <div className="bg-gray-50 rounded-md p-4 max-h-32 overflow-y-auto space-y-2">
              {selectedMachines.map((entry) => (
                <div key={entry.machine.id} className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-4 h-4 rounded border-2 border-purple-600 bg-purple-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">{entry.machine.name}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMachineForDetails(entry)}
                    className="flex-shrink-0 h-7 px-2 text-xs text-gray-600 hover:text-gray-900"
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm font-medium text-red-900">Error Loading Data</div>
              <div className="text-sm text-red-700 mt-1">{apiError}</div>
              <div className="text-xs text-red-600 mt-2">
                Please refresh the page or contact support if this issue persists.
              </div>
            </div>
          )}

          {pricePerSKU > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="text-sm font-medium text-blue-900">Pricing</div>
              <div className="text-2xl font-bold text-blue-700">£{pricePerSKU.toFixed(2)}</div>
              <div className="text-xs text-blue-600">per SKU / per machine / per month</div>
              {selectedSkus.size > 0 && (
                <div className="mt-2 text-sm text-blue-800">
                  Total: £{(pricePerSKU * selectedSkus.size * selectedMachines.length).toFixed(2)} / month
                  <div className="text-xs text-blue-600">
                    ({selectedSkus.size} SKUs × {selectedMachines.length} machine{selectedMachines.length > 1 ? 's' : ''})
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Select SKU Positions ({selectedSkus.size} selected)
            </h3>
            {availability && (
              <div className="mb-2 text-sm text-gray-600">
                {availability.totalAvailable} of {availability.totalCapacity} positions available
                {availability.byCategory && Object.keys(availability.byCategory).length > 0 && (
                  <div className="text-xs mt-1">
                    {Object.entries(availability.byCategory).map(([cat, counts]: [string, any]) => (
                      <span key={cat} className="mr-3">
                        {cat}: {counts.available}/{counts.total}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="border rounded-lg p-4 bg-gray-50 overflow-x-auto">
              <div className="min-w-[600px]">
                {Array.from({ length: planogramConfig.rows }, (_, row) => (
                  <div key={row} className="flex gap-2 mb-2">
                    {Array.from({ length: planogramConfig.columns }, (_, col) => {
                      const key = `${row},${col}`;
                      const isSelected = selectedSkus.has(key);
                      const isAllocated = availability?.allocations?.some(
                        alloc => alloc.rowIndex === row && alloc.columnIndex === col && alloc.status === 'approved'
                      ) || false;
                      const category = getCategoryForPosition(row, col);
                      const bgColor = getCategoryColor(category);
                      
                      return (
                        <button
                          key={col}
                          type="button"
                          onClick={() => toggleSku(row, col)}
                          disabled={isAllocated}
                          className={`
                            w-16 h-16 rounded border-2 text-xs font-medium flex flex-col items-center justify-center
                            transition-all
                            ${isAllocated ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-400' :
                              isSelected ? 'bg-blue-600 text-white border-blue-700 shadow-lg' :
                              `${bgColor} border-gray-300 hover:border-blue-400 hover:shadow`}
                          `}
                        >
                          <div className="text-[10px]">{row},{col}</div>
                          {category && <div className="text-[9px] mt-1">{category.substring(0, 8)}</div>}
                          {isAllocated && <div className="text-[9px] mt-1">Taken</div>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reservationType" className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Reservation Type
              </Label>
              <Select value={reservationType} onValueChange={setReservationType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select reservation type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Long term listing">Long term listing</SelectItem>
                  <SelectItem value="Retail media">Retail media</SelectItem>
                  <SelectItem value="DOOH advertising">DOOH advertising</SelectItem>
                  <SelectItem value="Short term listing campaign">Short term listing campaign</SelectItem>
                  <SelectItem value="Campaign full takeover unit">Campaign full takeover unit</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reservationType === 'Custom' && (
              <div>
                <Label htmlFor="customReservationType">
                  Custom Reservation Type
                </Label>
                <Input
                  id="customReservationType"
                  value={customReservationType}
                  onChange={(e) => setCustomReservationType(e.target.value)}
                  placeholder="Enter your custom reservation type..."
                  className="mt-1"
                />
              </div>
            )}

            {/* Show launch date and timeframe if set by admin */}
            {(launchDate || timeframe) && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-4 space-y-2">
                <h4 className="font-medium text-purple-900">Machine Launch Information</h4>
                {launchDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-700" />
                    <span className="text-sm text-purple-900">
                      <strong>Go Live Date:</strong> {new Date(launchDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                )}
                {timeframe && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-purple-700" />
                    <span className="text-sm text-purple-900">
                      <strong>Timeframe:</strong> {timeframe}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Show date inputs only if launch date is not set */}
            {!launchDate && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="requestedStart" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Requested Start Date (Optional)
                  </Label>
                  <Input
                    id="requestedStart"
                    type="date"
                    value={requestedStart}
                    onChange={(e) => setRequestedStart(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Admin will set final dates</p>
                </div>

                <div>
                  <Label htmlFor="requestedEnd" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Requested End Date (Optional)
                  </Label>
                  <Input
                    id="requestedEnd"
                    type="date"
                    value={requestedEnd}
                    onChange={(e) => setRequestedEnd(e.target.value)}
                    min={requestedStart || undefined}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Admin will set final dates</p>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes (Optional)
              </Label>
              <p className="text-xs text-gray-600 mt-1 mb-2">
                <span className="font-semibold text-blue-600">Please mention here if you need more SKUs than available or longer-term retail media listings.</span>
              </p>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional information for the admin... (e.g., requesting more SKUs than available, longer-term retail media listings, etc.)"
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Your tender reservation request will be reviewed by an admin. 
              You will be notified once it's approved or denied.
              {launchDate && timeframe 
                ? ` The machine is scheduled to go live on ${new Date(launchDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} with a ${timeframe} timeframe.`
                : ' The final reservation dates will be set by the admin.'
              }
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || selectedMachines.length === 0 || selectedSkus.size === 0}
              className="flex-1"
            >
              {submitting ? 'Submitting...' : 'Submit Tender Request'}
            </Button>
          </div>
        </form>
      </div>

      {/* Machine Details Dialog */}
      <Dialog open={!!selectedMachineForDetails} onOpenChange={(open) => {
        if (!open) {
          setSelectedMachineForDetails(null);
          setSkuAvailability(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {selectedMachineForDetails && (() => {
            const { machine, airport } = selectedMachineForDetails;
            const areaImage = machine.MachineMedia?.find(m => m.kind === 'area');
            const renderImage = machine.MachineMedia?.find(m => m.kind === 'render');
            const hasBothImages = areaImage && renderImage;
            const singleImage = areaImage || renderImage || machine.MachineMedia?.[0];
            
            // Determine display status
            let displayStatus: string = machine.status || 'Live/Available';
            if (machine.reservationInfo?.userReservationStatus === 'pending') {
              displayStatus = 'Pending';
            } else if (machine.reservationInfo?.userReservationStatus === 'approved') {
              if (machine.requiresTenderProcess && machine.reservationInfo?.isPlanogramFullyReserved) {
                displayStatus = 'Reserved';
              } else if (!machine.requiresTenderProcess) {
                displayStatus = 'Reserved';
              }
            }

            return (
              <>
                <DialogHeader className="px-6 pt-6 pb-4 border-b">
                  <DialogTitle className="text-2xl font-semibold">{machine.name}</DialogTitle>
                </DialogHeader>
                
                <div className="overflow-y-auto flex-1 px-6 py-4">
                  {/* Image Section */}
                  <div className="mb-6">
                    {hasBothImages ? (
                      <div className="relative">
                        <BeforeAfterSlider
                          beforeImage={areaImage.url}
                          afterImage={renderImage.url}
                          beforeLabel="Area"
                          afterLabel="Render"
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {airport?.code || '—'}
                        </div>
                      </div>
                    ) : singleImage ? (
                      <div className="relative w-full h-96 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={singleImage.url}
                          alt={machine.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {airport?.code || '—'}
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-full h-96 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl font-semibold text-blue-700 mb-2">{airport?.code || '—'}</div>
                          <p className="text-sm text-blue-600">No Image Available</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Details Section */}
                  <div className="space-y-6">
                    {/* Header Info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {renderStatusPill(displayStatus)}
                        {machine.availableSkus && machine.availableSkus > 0 && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            {machine.availableSkus} SKU{machine.availableSkus > 1 ? 's' : ''}
                          </span>
                        )}
                        {machine.requiresTenderProcess && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                            New Location
                          </span>
                        )}
                        {machine.label && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                            {machine.label}
                          </span>
                        )}
                      </div>
                      
                      {machine.description && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {machine.description}
                        </p>
                      )}
                      
                      {machine.costCents > 0 && (
                        <p className="text-base font-semibold text-slate-900">
                          Cost: {formatCurrency(machine.costCents)}
                        </p>
                      )}
                    </div>

                    {/* Technical Details Grid */}
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Status</p>
                        <p className="font-medium text-slate-900">{machine.status || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Available SKUs</p>
                        <p className="font-medium text-slate-900">
                          {machine.availableSkus !== null && machine.availableSkus !== undefined ? machine.availableSkus : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Capacity</p>
                        <p className="font-medium text-slate-900">{machine.capacity || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Machine Type</p>
                        <p className="font-medium text-slate-900">{machine.machineType || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Resolution</p>
                        <p className="font-medium text-slate-900">{machine.resolution || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Format</p>
                        <p className="font-medium text-slate-900">{machine.format || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Screen Size</p>
                        <p className="font-medium text-slate-900">{machine.screenSize || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Playback Format</p>
                        <p className="font-medium text-slate-900">{machine.playbackFormat || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Duration</p>
                        <p className="font-medium text-slate-900">{machine.duration || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Orientation</p>
                        <p className="font-medium text-slate-900">{machine.orientation || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Aspect Ratio</p>
                        <p className="font-medium text-slate-900">{machine.aspectRatio || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Type</p>
                        <p className="font-medium text-slate-900">{machine.venueType || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Name</p>
                        <p className="font-medium text-slate-900">{machine.venueName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Location</p>
                        <p className="font-medium text-slate-900">{machine.location || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Availability Type</p>
                        <p className="font-medium text-slate-900">{machine.availabilityType || '—'}</p>
                      </div>
                      {machine.requiresTenderProcess && machine.tenderTimeframe && (
                        <div>
                          <p className="text-xs uppercase text-slate-400 mb-1">Timeframe</p>
                          <p className="font-medium text-slate-900">{machine.tenderTimeframe}</p>
                        </div>
                      )}
                      {machine.requiresTenderProcess && machine.tenderLaunchDate && (
                        <div>
                          <p className="text-xs uppercase text-slate-400 mb-1">Launch Date</p>
                          <p className="font-medium text-slate-900">
                            {new Date(machine.tenderLaunchDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Planogram Configuration Section */}
                    {machine.requiresTenderProcess && machine.planogramConfig && (() => {
                      let planogramConfig: PlanogramConfig | null = null;
                      try {
                        planogramConfig = typeof machine.planogramConfig === 'string' 
                          ? JSON.parse(machine.planogramConfig)
                          : machine.planogramConfig;
                      } catch (e) {
                        console.error('Error parsing planogram config:', e);
                      }

                      if (!planogramConfig) return null;

                      const config = planogramConfig;

                      const getCategoryForPosition = (row: number, col: number): string | undefined => {
                        return config.positions.find(p => p.row === row && p.col === col)?.category;
                      };

                      const getCategoryColor = (category: string | undefined): string => {
                        if (!category) return 'bg-gray-100';
                        const colors: Record<string, string> = {
                          'Energy Drinks': 'bg-yellow-100',
                          'Snacks': 'bg-orange-100',
                          'Beverages': 'bg-blue-100',
                          'Healthy Options': 'bg-green-100',
                          'Confectionery': 'bg-pink-100',
                        };
                        return colors[category] || 'bg-purple-100';
                      };

                      // Group positions by category for summary
                      const categoryCounts = new globalThis.Map<string, number>();
                      config.positions.forEach(pos => {
                        if (pos.category) {
                          categoryCounts.set(pos.category, (categoryCounts.get(pos.category) || 0) + 1);
                        }
                      });

                      return (
                        <div className="pt-4 border-t border-slate-200">
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-slate-900 mb-2">Planogram Configuration</p>
                            <p className="text-xs text-slate-500 mb-4">
                              This machine has {config.positions.length} SKU positions configured across {categoryCounts.size} {categoryCounts.size === 1 ? 'category' : 'categories'}.
                            </p>
                            {categoryCounts.size > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {Array.from(categoryCounts.entries()).map(([category, count]) => (
                                  <span
                                    key={category}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getCategoryColor(category)} border border-slate-200`}
                                  >
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getCategoryColor(category).replace('bg-', '').replace('-100', '-600') }}></span>
                                    {category}: {count} SKU{count !== 1 ? 's' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="border rounded-lg p-4 bg-gray-50 overflow-x-auto">
                            <div className="min-w-[600px]">
                              {Array.from({ length: config.rows }, (_, row) => (
                                <div key={row} className="flex gap-2 mb-2">
                                  {Array.from({ length: config.columns }, (_, col) => {
                                    const category = getCategoryForPosition(row, col);
                                    const bgColor = getCategoryColor(category);
                                    
                                    // Check if this position is allocated
                                    const allocation = skuAvailability?.allocations?.find(
                                      alloc => alloc.rowIndex === row && alloc.columnIndex === col
                                    );
                                    const isPending = allocation?.status === 'pending';
                                    const isApproved = allocation?.status === 'approved';
                                    
                                    return (
                                      <div
                                        key={col}
                                        className={`
                                          w-16 h-16 rounded border-2 text-xs font-medium flex flex-col items-center justify-center relative
                                          ${isApproved ? 'bg-gray-300 opacity-60' : bgColor}
                                          ${isPending ? 'border-yellow-500 border-3 shadow-md' : 'border-gray-300'}
                                          ${isApproved ? 'border-gray-400' : ''}
                                        `}
                                        title={isPending ? 'Pending Request' : isApproved ? 'Approved' : 'Available'}
                                      >
                                        <div className="text-[10px] text-slate-600">{row},{col}</div>
                                        {category && (
                                          <div className="text-[9px] mt-1 text-slate-700 font-medium px-1 text-center leading-tight">
                                            {category.substring(0, 8)}
                                          </div>
                                        )}
                                        {isPending && (
                                          <div className="absolute top-0 right-0 w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
                                        )}
                                        {isApproved && (
                                          <div className="absolute top-0 right-0 w-3 h-3 bg-gray-500 rounded-full border border-white"></div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                            {(skuAvailability?.allocations?.some(a => a.status === 'pending') || skuAvailability?.allocations?.some(a => a.status === 'approved')) && (
                              <div className="mt-4 flex items-center gap-4 text-xs text-slate-600">
                                {skuAvailability?.allocations?.some(a => a.status === 'pending') && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
                                    <span>Pending Request</span>
                                  </div>
                                )}
                                {skuAvailability?.allocations?.some(a => a.status === 'approved') && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-gray-500 rounded-full border border-white"></div>
                                    <span>Approved</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Address Section */}
                    {(machine.addressLine1 || machine.city || machine.postCode) && (
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-xs uppercase text-slate-400 mb-2">Address</p>
                        <p className="text-sm font-medium text-slate-900">
                          {[machine.addressLine1, machine.addressLine2, machine.city, machine.county, machine.postCode]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

